import {inject, Injectable} from '@angular/core';
import {getCurrencySymbol} from '@angular/common';
import {filter, finalize, first, Observable, of, race, switchMap, throwError} from 'rxjs';

import {CashAmountPrompt} from '../../domain/ports/ui/cash-amount-prompt.port';
import {
  OrganizationRepositoryAdapter
} from '@core/entities/organization/infrastructure/adapters/organization-repository.adapter';
import {
  GlobalDynamicFvModalLoaderService
} from '@shared/services/global-dynamic-fv-modal-loader/global-dynamic-fv-modal-loader.service';
import {CashKeyboardComponent} from '../../presentation/components/cash-keyboard/cash-keyboard.component';
import {PaymentCancelledError} from '../../domain/model/payment.errors';
import {PAYMENT_SETTINGS} from '../../domain/ports/ui/payment-settings.port';

@Injectable({providedIn: 'root'})
export class CashAmountPromptHandler implements CashAmountPrompt {
  private readonly modalLoader = inject(GlobalDynamicFvModalLoaderService);
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly paymentSettings = inject(PAYMENT_SETTINGS);

  askAmount(totalAmount: number): Observable<number> {
    if (this.paymentSettings.isFastCashPaymentEnabled()) {
      return of(totalAmount);
    }

    return this.organizationService.getCurrentOrganization().pipe(
      first(),
      switchMap((org) => {
        const modalRef = this.modalLoader.openModal(
          CashKeyboardComponent,
          {
            data: {
              totalAmount,
              currencySymbol: getCurrencySymbol(org?.currency ?? 'EUR', 'wide'),
            },
          }
        );

        return race(
          modalRef.instance.confirmedDeliveredAmount,
          modalRef.instance.openStateChange.pipe(
            filter(isOpen => !isOpen),
            switchMap(() => throwError(() => new PaymentCancelledError()))
          )
        ).pipe(
          first(),
          finalize(() => modalRef.instance.close())
        );
      })
    );
  }
}
